import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import * as UserProfileSelectors from '../../store/user-profile/user-profile.selector';
import * as AuthSelectors from '../../store/auth/auth.selectors';
import * as AuthActions from '../../store/auth/auth.actions';
import * as BusinessActions from '../../store/business/business.actions';
import * as BusinessSelectors from '../../store/business/business.selectors';
import * as CartSelectors from '../../store/cart/cart.selectors';
import * as CartActions from '../../store/cart/cart.actions';
import { StoreCart, CartItem } from '../../store/cart/cart.models';
import {
  GeocodingService,
  UserAddress,
} from 'src/app/services/geocoding/geocoding.service';
import { BusinessService } from 'src/app/services/business/business.service';
import {
  QuerySuggestionsService,
  AutocompleteItem,
} from 'src/app/services/algolia/query-suggestions.service';
import { AlgoliaInsightsService } from 'src/app/services/algolia/insights.service';
import {
  ALGOLIA_CONFIG,
  ALGOLIA_INDICES,
} from 'src/app/core/config/algolia.config';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import algoliasearch, { SearchClient } from 'algoliasearch';
import {
  Observable,
  combineLatest,
  BehaviorSubject,
  Subject,
  fromEvent,
} from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs/operators';
import { of } from 'rxjs';

interface NavbarViewModel {
  userProfile: any;
  isAuthenticated: boolean;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('autocompleteContainer', { static: false })
  autocompleteContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileSearchInput', { static: false })
  mobileSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileAutocompleteContainer', { static: false })
  mobileAutocompleteContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('userDropdownContainer', { static: false })
  userDropdownContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('userDetailsLink', { static: false })
  userDetailsLink?: ElementRef<HTMLAnchorElement>;
  @ViewChild('cartDropdownContainer', { static: false })
  cartDropdownContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('cartLink', { static: false })
  cartLink?: ElementRef<HTMLAnchorElement>;
  protected readonly userProfile$ = this.store.select(
    UserProfileSelectors.selectCurrentUserProfile
  );
  protected readonly isAuthenticated$ = this.store.select(
    AuthSelectors.selectIsAuthenticated
  );

  readonly vm$: Observable<NavbarViewModel> = combineLatest([
    this.userProfile$,
    this.isAuthenticated$,
  ]).pipe(
    map(([userProfile, isAuthenticated]) => ({
      userProfile,
      isAuthenticated,
    }))
  );

  zipCodeForm: FormGroup;
  currentZipCode$ = this.store.select(BusinessSelectors.selectCurrentZipCode);
  currentLocationName$ = new BehaviorSubject<string>('Brooklyn, NY');
  isLoadingLocation = false;
  showZipCodeInput = false;
  showMobileZipCodeInput = false;
  showUserDropdown = false;
  showCartDropdown = false;
  showMobileSearchModal = false;
  isUserAvatarLoading = true;
  storeLogoLoadingStates: Map<string, boolean> = new Map();
  mobileStoreLogoLoadingStates: Map<string, boolean> = new Map();
  private currentLatitude: number | null = null;
  private currentLongitude: number | null = null;

  // Algolia search state
  searchTerm: string = '';
  querySuggestions: AutocompleteItem[] = [];
  searchResults: BusinessInformationEntity[] = [];
  showSuggestions: boolean = false;
  showResults: boolean = false;
  isLoadingSearch: boolean = false;
  lastQueryID: string | null = null;
  private searchClient: SearchClient;
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Mobile search state
  mobileSearchTerm: string = '';
  mobileQuerySuggestions: AutocompleteItem[] = [];
  mobileSearchResults: BusinessInformationEntity[] = [];
  showMobileSuggestions: boolean = false;
  showMobileResults: boolean = false;
  isLoadingMobileSearch: boolean = false;
  private mobileSearchSubject = new Subject<string>();

  // Cart state
  storeCarts$: Observable<StoreCart[]> = this.store.select(
    CartSelectors.selectStoreCartsArray
  );
  cartItemCount$: Observable<number> = this.store.select(
    CartSelectors.selectCartItemCount
  );
  cartTotalPrice$: Observable<number> = this.store.select(
    CartSelectors.selectCartTotalPrice
  );
  isCartEmpty$: Observable<boolean> = this.store.select(
    CartSelectors.selectIsCartEmpty
  );

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    private router: Router,
    private geocodingService: GeocodingService,
    private businessService: BusinessService,
    private querySuggestionsService: QuerySuggestionsService,
    private insightsService: AlgoliaInsightsService
  ) {
    this.zipCodeForm = this.fb.group({
      zipCode: ['', [Validators.pattern(/^\d{5}$/)]], // Not required - can be empty
    });

    // Initialize Algolia search client
    this.searchClient = algoliasearch(
      ALGOLIA_CONFIG.appId,
      ALGOLIA_CONFIG.searchApiKey
    );
  }

  ngOnInit(): void {
    this.getCurrentLocation();
    this.subscribeToZipCodeChanges();
    this.setupSearchInput();
    this.setupMobileSearchInput();
  }

  ngAfterViewInit(): void {
    // Setup click outside to close autocomplete
    if (typeof document !== 'undefined') {
      fromEvent(document, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: Event) => {
          const target = event.target as HTMLElement;
          // Desktop autocomplete
          if (
            this.autocompleteContainer?.nativeElement &&
            !this.autocompleteContainer.nativeElement.contains(target) &&
            this.searchInput?.nativeElement &&
            !this.searchInput.nativeElement.contains(target)
          ) {
            this.showSuggestions = false;
            this.showResults = false;
          }
          // Mobile autocomplete
          if (
            this.mobileAutocompleteContainer?.nativeElement &&
            !this.mobileAutocompleteContainer.nativeElement.contains(target) &&
            this.mobileSearchInput?.nativeElement &&
            !this.mobileSearchInput.nativeElement.contains(target)
          ) {
            this.showMobileSuggestions = false;
            this.showMobileResults = false;
          }
          // User dropdown
          if (
            this.userDropdownContainer?.nativeElement &&
            !this.userDropdownContainer.nativeElement.contains(target) &&
            this.userDetailsLink?.nativeElement &&
            !this.userDetailsLink.nativeElement.contains(target)
          ) {
            this.showUserDropdown = false;
          }
          // Cart dropdown
          if (
            this.cartDropdownContainer?.nativeElement &&
            !this.cartDropdownContainer.nativeElement.contains(target) &&
            this.cartLink?.nativeElement &&
            !this.cartLink.nativeElement.contains(target)
          ) {
            this.showCartDropdown = false;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup search input with debouncing
   */
  setupSearchInput(): void {
    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query: string) => {
        if (query.length >= 2) {
          this.loadQuerySuggestions(query);
          this.searchBusinesses(query);
        } else {
          this.querySuggestions = [];
          this.searchResults = [];
          this.showSuggestions = false;
          this.showResults = false;
        }
      });
  }

  /**
   * Handle search input changes
   */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value.trim();

    this.searchTerm = query;

    if (query.length === 0) {
      this.querySuggestions = [];
      this.searchResults = [];
      this.showSuggestions = false;
      this.showResults = false;
      return;
    }

    // Emit to debounced subject
    this.searchSubject.next(query);
  }

  /**
   * Load query suggestions
   */
  async loadQuerySuggestions(query: string): Promise<void> {
    try {
      const suggestions = await this.querySuggestionsService.searchSuggestions(
        query,
        5
      );
      this.querySuggestions = suggestions;
      this.showSuggestions = suggestions.length > 0;
    } catch (error) {
      console.error('Error loading query suggestions:', error);
      this.querySuggestions = [];
      this.showSuggestions = false;
    }
  }

  /**
   * Search businesses using Algolia
   */
  async searchBusinesses(query: string): Promise<void> {
    if (!query || query.trim().length < 2) {
      this.searchResults = [];
      this.showResults = false;
      return;
    }

    this.isLoadingSearch = true;
    this.showResults = true;

    try {
      const index = this.searchClient.initIndex(ALGOLIA_INDICES.BUSINESSES);
      const result = await index.search(query, {
        hitsPerPage: 5,
        getRankingInfo: true,
      });

      // Track query for analytics
      if (result.queryID) {
        this.lastQueryID = result.queryID;
        await this.querySuggestionsService.trackQuery(query, result.nbHits);
      }

      // Map Algolia results to BusinessInformationEntity
      this.searchResults = (result.hits as any[]).map((hit) => ({
        ...hit,
        id: hit.objectID, // Map objectID to id
      })) as BusinessInformationEntity[];

      // Initialize loading states for store logos
      this.searchResults.forEach((business) => {
        this.storeLogoLoadingStates.set(business.id, true);
      });

      this.isLoadingSearch = false;
    } catch (error) {
      console.error('Error searching businesses:', error);
      this.searchResults = [];
      this.isLoadingSearch = false;
    }
  }

  /**
   * Handle suggestion click
   */
  onSuggestionClick(suggestion: AutocompleteItem): void {
    this.searchTerm = suggestion.query;
    if (this.searchInput) {
      this.searchInput.nativeElement.value = suggestion.query;
    }
    this.searchBusinesses(suggestion.query);
    this.showSuggestions = false;
  }

  /**
   * Handle search result click
   */
  onSearchResultClick(
    business: BusinessInformationEntity,
    index: number
  ): void {
    if (this.lastQueryID) {
      this.querySuggestionsService.trackResultClick({
        query: this.searchTerm,
        queryID: this.lastQueryID,
        objectID: business.id,
        position: index + 1,
      });
    }

    this.router.navigate(['/businesses', business.id]);
    this.clearSearch();
  }

  /**
   * Handle search form submit
   */
  onSearchSubmit(event: Event): void {
    event.preventDefault();
    if (this.searchTerm.trim().length >= 2) {
      this.searchBusinesses(this.searchTerm);
      this.showSuggestions = false;
    }
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.querySuggestions = [];
    this.searchResults = [];
    this.showSuggestions = false;
    this.showResults = false;
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
  }

  /**
   * Handle search input focus
   */
  onSearchInputFocus(): void {
    if (this.searchTerm.length >= 2) {
      this.showSuggestions = this.querySuggestions.length > 0;
      this.showResults = this.searchResults.length > 0;
    } else if (this.searchTerm.length === 0) {
      // Show recent queries if available
      const recentQueries =
        this.querySuggestionsService.getRecentQueryStrings(5);
      if (recentQueries.length > 0) {
        this.querySuggestions = recentQueries.map((q) => ({
          query: q,
          highlighted: q,
          popularity: 0,
        }));
        this.showSuggestions = true;
      }
    }
  }

  /**
   * Setup mobile search input with debouncing
   */
  setupMobileSearchInput(): void {
    // Setup debounced mobile search
    this.mobileSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query: string) => {
        if (query.length >= 2) {
          this.loadMobileQuerySuggestions(query);
          this.searchMobileBusinesses(query);
        } else {
          this.mobileQuerySuggestions = [];
          this.mobileSearchResults = [];
          this.showMobileSuggestions = false;
          this.showMobileResults = false;
        }
      });
  }

  /**
   * Handle mobile search input changes
   */
  onMobileSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value.trim();

    this.mobileSearchTerm = query;

    if (query.length === 0) {
      this.mobileQuerySuggestions = [];
      this.mobileSearchResults = [];
      this.showMobileSuggestions = false;
      this.showMobileResults = false;
      return;
    }

    // Emit to debounced subject
    this.mobileSearchSubject.next(query);
  }

  /**
   * Load mobile query suggestions
   */
  async loadMobileQuerySuggestions(query: string): Promise<void> {
    try {
      const suggestions = await this.querySuggestionsService.searchSuggestions(
        query,
        5
      );
      this.mobileQuerySuggestions = suggestions;
      this.showMobileSuggestions = suggestions.length > 0;
    } catch (error) {
      console.error('Error loading mobile query suggestions:', error);
      this.mobileQuerySuggestions = [];
      this.showMobileSuggestions = false;
    }
  }

  /**
   * Search businesses using Algolia for mobile
   */
  async searchMobileBusinesses(query: string): Promise<void> {
    if (!query || query.trim().length < 2) {
      this.mobileSearchResults = [];
      this.showMobileResults = false;
      return;
    }

    this.isLoadingMobileSearch = true;
    this.showMobileResults = true;

    try {
      const index = this.searchClient.initIndex(ALGOLIA_INDICES.BUSINESSES);
      const result = await index.search(query, {
        hitsPerPage: 5,
      });

      // Track query
      if (result.queryID) {
        await this.querySuggestionsService.trackQuery(query, result.nbHits);
      }

      // Map Algolia results to BusinessInformationEntity
      this.mobileSearchResults = (result.hits as any[]).map((hit) => ({
        ...hit,
        id: hit.objectID, // Map objectID to id
      })) as BusinessInformationEntity[];

      // Initialize loading states for mobile store logos
      this.mobileSearchResults.forEach((business) => {
        this.mobileStoreLogoLoadingStates.set(business.id, true);
      });

      this.isLoadingMobileSearch = false;
    } catch (error) {
      console.error('Error searching mobile businesses:', error);
      this.mobileSearchResults = [];
      this.isLoadingMobileSearch = false;
    }
  }

  /**
   * Handle mobile suggestion click
   */
  onMobileSuggestionClick(suggestion: AutocompleteItem): void {
    this.mobileSearchTerm = suggestion.query;
    if (this.mobileSearchInput) {
      this.mobileSearchInput.nativeElement.value = suggestion.query;
    }
    this.searchMobileBusinesses(suggestion.query);
    this.showMobileSuggestions = false;
    // Don't close modal here, let user see the results
  }

  /**
   * Handle mobile search result click
   */
  onMobileSearchResultClick(
    business: BusinessInformationEntity,
    index: number
  ): void {
    if (this.lastQueryID) {
      this.querySuggestionsService.trackResultClick({
        query: this.mobileSearchTerm,
        queryID: this.lastQueryID,
        objectID: business.id,
        position: index + 1,
      });
    }
    this.router.navigate(['/businesses', business.id]);
    this.closeMobileSearchModal();
  }

  /**
   * Handle mobile search form submit
   */
  onMobileSearchSubmit(event: Event): void {
    event.preventDefault();
    if (this.mobileSearchTerm.trim().length >= 2) {
      this.searchMobileBusinesses(this.mobileSearchTerm);
      this.showMobileSuggestions = false;
    }
  }

  /**
   * Clear mobile search
   */
  clearMobileSearch(): void {
    this.mobileSearchTerm = '';
    this.mobileQuerySuggestions = [];
    this.mobileSearchResults = [];
    this.showMobileSuggestions = false;
    this.showMobileResults = false;
    if (this.mobileSearchInput) {
      this.mobileSearchInput.nativeElement.value = '';
    }
  }

  /**
   * Handle mobile search input focus
   */
  onMobileSearchInputFocus(): void {
    if (this.mobileSearchTerm.length >= 2) {
      this.showMobileSuggestions = this.mobileQuerySuggestions.length > 0;
      this.showMobileResults = this.mobileSearchResults.length > 0;
    } else if (this.mobileSearchTerm.length === 0) {
      // Show recent queries if available
      const recentQueries =
        this.querySuggestionsService.getRecentQueryStrings(5);
      if (recentQueries.length > 0) {
        this.mobileQuerySuggestions = recentQueries.map((q) => ({
          query: q,
          highlighted: q,
          popularity: 0,
        }));
        this.showMobileSuggestions = true;
      }
    }
  }

  getCurrentLocation(): void {
    if (navigator.geolocation) {
      this.isLoadingLocation = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          this.currentLatitude = latitude;
          this.currentLongitude = longitude;
          this.updateLocationNameFromCoordinates(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          this.isLoadingLocation = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  }

  updateLocationNameFromCoordinates(latitude: number, longitude: number): void {
    this.geocodingService.reverseGeocode(latitude, longitude).subscribe({
      next: (address) => {
        console.log(
          '🚀 ~ NavbarComponent ~ updateLocationNameFromCoordinates ~ address:',
          address
        );
        if (address) {
          this.currentLocationName$.next(address.address);
        }
        this.isLoadingLocation = false;
      },
      error: (error) => {
        console.error('Error reverse geocoding:', error);
        this.isLoadingLocation = false;
      },
    });
  }

  private isUserTyping = false;

  subscribeToZipCodeChanges(): void {
    this.currentZipCode$.subscribe((zipCode) => {
      // Only update form value if user is not currently typing
      if (!this.isUserTyping && zipCode) {
        this.zipCodeForm.patchValue({ zipCode }, { emitEvent: false });
        this.updateLocationNameFromZipCode(zipCode);
      } else if (!zipCode && !this.isUserTyping) {
        // If zipcode is cleared/empty, use current location
        this.zipCodeForm.patchValue({ zipCode: '' }, { emitEvent: false });
        if (this.currentLatitude !== null && this.currentLongitude !== null) {
          this.getCurrentLocation();
          this.store.dispatch(
            BusinessActions.loadBusinessesByLocationPaginated({
              latitude: this.currentLatitude,
              longitude: this.currentLongitude,
              pageNumber: 1,
              limit: 6,
              storeType: null
            })
          );
        }
      }
    });
  }
  updateLocationNameFromZipCode(zipCode: string): void {
    this.isLoadingLocation = true;
    this.businessService
      .getZipCodeInfo(zipCode)
      .pipe(
        switchMap((zipCodeInfo) =>
          this.geocodingService.reverseGeocode(
            zipCodeInfo.latitude,
            zipCodeInfo.longitude
          )
        ),
        catchError((error) => {
          console.error('Error getting location from zipcode:', error);
          this.isLoadingLocation = false;
          return of(null);
        })
      )
      .subscribe({
        next: (address) => {
          if (address) {
            this.currentLocationName$.next(address.address);
          }
          this.isLoadingLocation = false;
        },
      });
  }

  onZipCodeSubmit(): void {
    const zipCode = this.zipCodeForm.value.zipCode?.trim();

    // If zipcode is empty or invalid, use current location
    if (!zipCode || this.zipCodeForm.get('zipCode')?.invalid) {
      if (this.currentLatitude !== null && this.currentLongitude !== null) {
        // Use stored coordinates
        this.store.dispatch(
            BusinessActions.loadBusinessesByLocationPaginated({
            latitude: this.currentLatitude,
            longitude: this.currentLongitude,
              pageNumber: 1,
              limit: 6,
              storeType: null
          })
        );
        this.showZipCodeInput = false;
        this.showMobileZipCodeInput = false;
      } else {
        // Request location if not available
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            this.currentLatitude = latitude;
            this.currentLongitude = longitude;
            this.store.dispatch(
                BusinessActions.loadBusinessesByLocationPaginated({
                latitude,
                longitude,
                  pageNumber: 1,
                  limit: 6,
                  storeType: null
              })
            );
            this.showZipCodeInput = false;
            this.showMobileZipCodeInput = false;
          },
          (error) => {
            console.error('Error getting location for business search:', error);
            alert(
              'Please enable location access or enter a ZIP code to find businesses.'
            );
          }
        );
      }
      return;
    }

    // Valid zipcode provided
    console.log('🚀 ~ NavbarComponent ~ onZipCodeSubmit ~ zipCode:', zipCode);
    this.store.dispatch(BusinessActions.loadBusinessesByZipCode({ zipCode }));
    this.updateLocationNameFromZipCode(zipCode);
    this.showZipCodeInput = false;
    this.showMobileZipCodeInput = false;
    // Navigate to home if not already there
  }

  toggleUserDropdown(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showUserDropdown = !this.showUserDropdown;
  }

  closeUserDropdown(): void {
    this.showUserDropdown = false;
  }

  toggleCartDropdown(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showCartDropdown = !this.showCartDropdown;
  }

  closeCartDropdown(): void {
    this.showCartDropdown = false;
  }

  openMobileSearchModal(): void {
    this.showMobileSearchModal = true;
    // Focus on search input after modal opens
    setTimeout(() => {
      if (this.mobileSearchInput) {
        this.mobileSearchInput.nativeElement.focus();
      }
    }, 100);
  }

  closeMobileSearchModal(): void {
    this.showMobileSearchModal = false;
    this.clearMobileSearch();
  }

  signOut(): void {
    this.closeUserDropdown();
    this.store.dispatch(AuthActions.signOut());
  }

  getUserDisplayName(userProfile: any): string {
    if (!userProfile) {
      return 'Guest';
    }
    return (
      userProfile.displayName ||
      `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() ||
      'User'
    );
  }

  getUserPhotoUrl(userProfile: any): string {
    return userProfile?.photoURL || 'assets/img/user/user-icon.svg';
  }

  onUserAvatarLoad(): void {
    this.isUserAvatarLoading = false;
  }

  onUserAvatarError(): void {
    this.isUserAvatarLoading = false;
  }

  onStoreLogoLoad(businessId: string): void {
    this.storeLogoLoadingStates.set(businessId, false);
  }

  onStoreLogoError(businessId: string): void {
    this.storeLogoLoadingStates.set(businessId, false);
  }

  isStoreLogoLoading(businessId: string): boolean {
    return this.storeLogoLoadingStates.get(businessId) !== false;
  }

  onMobileStoreLogoLoad(businessId: string): void {
    this.mobileStoreLogoLoadingStates.set(businessId, false);
  }

  onMobileStoreLogoError(businessId: string): void {
    this.mobileStoreLogoLoadingStates.set(businessId, false);
  }

  isMobileStoreLogoLoading(businessId: string): boolean {
    return this.mobileStoreLogoLoadingStates.get(businessId) !== false;
  }

  removeCartItem(storeId: string, itemId: string): void {
    this.store.dispatch(CartActions.removeCartItem({ storeId, itemId }));
  }

  clearCart(): void {
    this.store.dispatch(CartActions.clearCart());
  }

  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    let appointmentDate: Date;

    if (typeof date === 'string') {
      appointmentDate = new Date(date);
    } else if (date instanceof Date) {
      appointmentDate = date;
    } else {
      appointmentDate = new Date(date as any);
    }

    if (isNaN(appointmentDate.getTime())) {
      return '';
    }

    return appointmentDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  formatTime(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    let appointmentDate: Date;

    if (typeof date === 'string') {
      appointmentDate = new Date(date);
    } else if (date instanceof Date) {
      appointmentDate = date;
    } else {
      appointmentDate = new Date(date as any);
    }

    if (isNaN(appointmentDate.getTime())) {
      return '';
    }

    return appointmentDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  getItemDescription(item: CartItem): string {
    const parts: string[] = [];

    // Add combo names
    if (item.combos && item.combos.length > 0) {
      const comboNames = item.combos.map((combo) => combo.name).join(', ');
      parts.push(comboNames);
    }

    // Add item names
    if (item.items && item.items.length > 0) {
      const itemNames = item.items.map((item) => item.name).join(', ');
      if (parts.length > 0) {
        parts.push(`+ ${itemNames}`);
      } else {
        parts.push(itemNames);
      }
    }

    // Add staff name
    if (item.staffName) {
      parts.push(`with ${item.staffName}`);
    }

    return parts.length > 0 ? parts.join(' ') : 'Service';
  }

  toggleZipCodeInput(): void {
    this.showZipCodeInput = !this.showZipCodeInput;
    // Focus on input when showing
    if (this.showZipCodeInput) {
      setTimeout(() => {
        const input = document.querySelector(
          'input[formControlName="zipCode"]'
        ) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 100);
    }
  }

  toggleMobileZipCodeInput(): void {
    this.showMobileZipCodeInput = !this.showMobileZipCodeInput;
    // Focus on mobile input when showing
    if (this.showMobileZipCodeInput) {
      setTimeout(() => {
        const mobileInput = document.querySelector(
          '.mobile-address input[formControlName="zipCode"]'
        ) as HTMLInputElement;
        if (mobileInput) {
          mobileInput.focus();
        }
      }, 100);
    }
  }

  useCurrentLocation(): void {
    // Clear zipcode from store
    this.store.dispatch(BusinessActions.clearBusinesses());

    // Clear the form
    this.zipCodeForm.patchValue({ zipCode: '' }, { emitEvent: false });

    if (this.currentLatitude !== null && this.currentLongitude !== null) {
      // Use stored coordinates
      this.store.dispatch(
            BusinessActions.loadBusinessesByLocationPaginated({
          latitude: this.currentLatitude,
          longitude: this.currentLongitude,
              pageNumber: 1,
              limit: 6,
              storeType: null
        })
      );
      this.showZipCodeInput = false;
      // Navigate to home if not already there
    } else {
      // Request location if not available
      this.isLoadingLocation = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          this.currentLatitude = latitude;
          this.currentLongitude = longitude;
          this.updateLocationNameFromCoordinates(latitude, longitude);
          this.store.dispatch(
                BusinessActions.loadBusinessesByLocationPaginated({
              latitude,
              longitude,
                  pageNumber: 1,
                  limit: 6,
                  storeType: null
            })
          );
          this.showZipCodeInput = false;
          // Navigate to home if not already there
        },
        (error) => {
          console.error('Error getting location for business search:', error);
          this.isLoadingLocation = false;
          alert('Please enable location access to find nearby businesses.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  }
}
